import type { RfqSubmission } from '../types';
import type { SubmissionQuoteGroup } from './rfqSubmissionHistory';

export const PENDING_REVISION_HINT = 'Revised quote';

export type QuoteStreamDisplay = {
  /** Approved submission when present, otherwise the newest upload. */
  primary: RfqSubmission;
  /** Newer unapproved uploads after an approved primary. */
  pendingRevisions: RfqSubmission[];
  /** Older history excluding primary and pending revisions. */
  previous: RfqSubmission[];
  /** True when {@link primary} is an approved submission. */
  hasApprovedPrimary: boolean;
};

function createdAtMs(sub: RfqSubmission): number {
  return sub.createdAt instanceof Date ? sub.createdAt.getTime() : 0;
}

function isApproved(sub: RfqSubmission, approvedIds: Set<string>): boolean {
  return !!sub.id && approvedIds.has(sub.id);
}

/**
 * Split a quote stream into what the supplier portal should show first vs history.
 * When staff approved an older quote and the supplier uploaded a newer revision,
 * the approved quote becomes primary — not buried under "previous quote".
 */
export function resolveQuoteStreamDisplay(
  group: SubmissionQuoteGroup,
  approvedIds: Set<string>,
): QuoteStreamDisplay {
  const all = [group.latest, ...group.previous].sort(
    (a, b) => createdAtMs(b) - createdAtMs(a),
  );
  const approvedInStream = all.filter(s => isApproved(s, approvedIds));

  if (approvedInStream.length > 0) {
    const primary = approvedInStream[0]!;
    const primaryMs = createdAtMs(primary);
    const pendingRevisions = all.filter(
      s => s !== primary && createdAtMs(s) > primaryMs && !isApproved(s, approvedIds),
    );
    const previous = all.filter(
      s => s !== primary && !pendingRevisions.includes(s),
    );
    return { primary, pendingRevisions, previous, hasApprovedPrimary: true };
  }

  return {
    primary: group.latest,
    pendingRevisions: [],
    previous: group.previous,
    hasApprovedPrimary: false,
  };
}

/** Primary submission per stream — use for sidebar totals and summary headings. */
export function primarySubmissionsFromGroups(
  groups: SubmissionQuoteGroup[],
  approvedIds: Set<string>,
): RfqSubmission[] {
  return groups.map(g => resolveQuoteStreamDisplay(g, approvedIds).primary);
}

/** At most one submission per quote stream should display as approved. */
export function canonicalApprovedSubmissionIdForStream(
  display: QuoteStreamDisplay,
  approvedIds: Set<string>,
): string | undefined {
  if (!display.hasApprovedPrimary || !display.primary.id) return undefined;
  return approvedIds.has(display.primary.id) ? display.primary.id : undefined;
}

export type VisibleQuoteRow = {
  sub: RfqSubmission;
  isPendingRevision: boolean;
  previous: RfqSubmission[];
  /** Show expandable previous-quote history on this row. */
  showPreviousHistory: boolean;
  /** When set, only this submission id should render the approved state. */
  canonicalApprovedId?: string;
};

/** Active quote rows for a stream — newest upload first. */
export function visibleQuoteRowsNewestFirst(
  display: QuoteStreamDisplay,
  approvedIds?: Set<string>,
): VisibleQuoteRow[] {
  const pendingSet = new Set(display.pendingRevisions);
  const canonicalApprovedId = approvedIds
    ? canonicalApprovedSubmissionIdForStream(display, approvedIds)
    : undefined;
  const allSubs = sortSubmissionsNewestFirst([
    display.primary,
    ...display.pendingRevisions,
    ...display.previous,
  ]);

  const seen = new Set<string>();
  const unique = allSubs.filter(sub => {
    const key = sub.id ?? sub.storagePath;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Always show each upload as its own row — never nest under "previous quote".
  return unique.map(sub => ({
    sub,
    isPendingRevision: pendingSet.has(sub),
    previous: [],
    showPreviousHistory: false,
    canonicalApprovedId,
  }));
}

export function sortSubmissionsNewestFirst(subs: RfqSubmission[]): RfqSubmission[] {
  return subs.slice().sort((a, b) => createdAtMs(b) - createdAtMs(a));
}
